import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { api, ErreurApi } from '../api'
import { useToast } from '../etat/Toast'

/**
 * Le bouton « Bug » — présent sur tout l'écran, à toute page.
 *
 * Objectif : que signaler un problème prenne dix secondes, pas un aller-retour
 * par SMS avec une photo d'écran floue. La capture se prend sur place, telle
 * que l'écran est au moment du clic — pas besoin de décrire ce qu'on voit, la
 * capture le montre déjà.
 */
export function SignalerBug() {
  const { pathname } = useLocation()
  const { signaler, alerter } = useToast()
  const [capture, setCapture] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [envoi, setEnvoi] = useState(false)

  const ouvrir = async () => {
    setEnCours(true)
    try {
      // Chargé à la demande : la plupart des sessions ne signalent jamais de
      // bug, inutile d'alourdir le chargement initial pour tout le monde.
      const { default: html2canvas } = await import('html2canvas-pro')
      const rendu = await html2canvas(document.body, {
        // Résolution native de l'écran (plafonnée à 2×) : un bug d'affichage
        // se lit mal si le texte de la capture est flou. Une capture d'iPad en
        // JPEG à cette qualité tient très large sous la limite de requête
        // (8 Mo) même à pleine résolution.
        scale: Math.min(window.devicePixelRatio || 1, 2),
        useCORS: true,
        backgroundColor: '#ffffff',
      })
      setCapture(rendu.toDataURL('image/jpeg', 0.85))
    } catch {
      alerter('Capture d’écran impossible sur cet appareil.')
    } finally {
      setEnCours(false)
    }
  }

  const fermer = () => {
    setCapture(null)
    setDescription('')
  }

  const envoyer = async () => {
    if (!capture || !description.trim() || envoi) return
    setEnvoi(true)
    try {
      await api('/bugs', { methode: 'POST', corps: { page: pathname, description, capture } })
      signaler('Signalement envoyé — merci !')
      fermer()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Envoi impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className="bouton-bug"
        onClick={() => void ouvrir()}
        disabled={enCours}
        title="Signaler un bug sur cet écran"
      >
        {enCours ? '…' : '🐛'}
      </button>

      {capture && (
        <div className="voile" onMouseDown={fermer}>
          <div
            className="connexion-boite"
            style={{ maxWidth: 480, width: '92%' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="etiquette">Signaler un bug</div>
            <img
              src={capture}
              alt="Capture de l’écran actuel"
              style={{
                width: '100%',
                border: '1px solid var(--color-neutral-300)',
                marginTop: 8,
              }}
            />
            <textarea
              autoFocus
              placeholder="Qu’est-ce qui ne va pas ? Soyez aussi précis que possible."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              style={{ width: '100%', marginTop: 12, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button type="button" className="btn btn-secondaire btn-large" onClick={fermer}>
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primaire btn-large"
                disabled={!description.trim() || envoi}
                onClick={() => void envoyer()}
              >
                {envoi ? 'Envoi…' : 'Envoyer le signalement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
